import { useState, useCallback, useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'

import { BasicButton } from '@/Atomic/Atoms/BasicButton'
import { Icon } from '@/Atomic/Atoms/Icon'
import { MenuItem } from '@/Atomic/Molecules/MenuItem'
import { MenuSheet } from '@/Atomic/Organisms/MenuSheet'

interface MobileFilterSelectOption {
  label: string
  value: string | boolean
}

interface MobileFilterSelectProps<TOption extends MobileFilterSelectOption> {
  name: string
  options: TOption[]
  value?: TOption
  onChange: (option: TOption) => void
  placeholder?: string
  title?: string
}

export const MobileFilterSelect = <TOption extends MobileFilterSelectOption>({
  name,
  options,
  value,
  onChange,
  placeholder,
  title,
}: MobileFilterSelectProps<TOption>) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const focusTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )

  const handleClose = useCallback(() => {
    setIsOpen(false)
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current)
    }
    focusTimeoutRef.current = setTimeout(() => {
      triggerRef.current?.focus()
      focusTimeoutRef.current = undefined
    }, 100)
  }, [])

  useEffect(
    () => () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current)
      }
    },
    [],
  )

  const handleSelect = useCallback(
    (option: TOption) => {
      onChange(option)
      handleClose()
    },
    [onChange, handleClose],
  )

  const triggerLabel = value?.label || placeholder || t('common.select')
  const selectedValue = value?.value

  return (
    <>
      <BasicButton
        ref={triggerRef}
        basicVariant="outlined"
        className="mobile-filter-select-trigger"
        onClick={() => setIsOpen(true)}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={triggerLabel}
      >
        <span className="mobile-filter-select-trigger-label">
          {triggerLabel}
        </span>
        <Icon icon="chevron-right" size="md" />
      </BasicButton>

      <MenuSheet
        isOpen={isOpen}
        position="bottom-left"
        onBackdropClick={handleClose}
        onMobileCloseClick={handleClose}
        panelClassName="mobile-filter-select-sheet"
      >
        <div className="mobile-filter-select-content">
          <div className="mobile-filter-select-body">
            {title && (
              <div className="mobile-filter-select-header">
                <button
                  className="mobile-filter-select-back"
                  onClick={handleClose}
                  aria-label={t('common.back')}
                >
                  <Icon icon="arrow-left" size="md" />
                </button>
                <h3 className="mobile-filter-select-title">{title}</h3>
              </div>
            )}
            <div className="mobile-filter-select-options">
              {options.map((option) => {
                const isSelected = option.value === selectedValue
                return (
                  <MenuItem
                    key={`${name}-${String(option.value)}`}
                    className={`mobile-filter-select-option ${isSelected ? 'mobile-filter-select-option--selected' : ''}`}
                    label={option.label}
                    plainLabel
                    rightIcon={isSelected ? 'check' : undefined}
                    onClick={() => handleSelect(option)}
                  />
                )
              })}
            </div>
          </div>
        </div>
      </MenuSheet>
    </>
  )
}
